import { PreflightResult, PreflightWarning, PreparedFile } from '../types';
import { checkMagicBytes, isImageDecodable, isImageFilename, isWithinSizeLimit, parseRowIndex } from './fileMatcher';
import { sha256OfFile } from './sha256';
import { countXlsDataRows } from './xlsParser';

const XLS_EXTENSIONS = new Set(['xlsx']);

// Backend XLS schema uses 3-row header (config/xls_import.php data_start_row=3).
// First data row is at XLS row 3, so filename prefix `3.png` matches first row.
const DATA_START_ROW = 3;

/**
 * Validate and partition the files dropped into the picker:
 *   • exactly one xlsx is required
 *   • each image must pass extension + magic-bytes + size + decodable
 *   • each image filename must encode a row_index
 *   • duplicate row_index → first by sorted filename wins, others warn
 *   • file with row_index outside the XLS data range warns (kept for
 *     batch upload; server skips it)
 *
 * Returns prepared files (with sha256) + a list of UI-friendly warnings.
 * The batch can still be uploaded if the warnings list is non-empty —
 * decisions about that are surfaced to the user.
 */
export async function preflight(files: FileList | File[]): Promise<PreflightResult | { error: string }> {
    const all: File[] = Array.from(files as File[]);

    const xlsCandidates = all.filter((f) => {
        const ext = f.name.toLowerCase().split('.').pop() ?? '';
        return XLS_EXTENSIONS.has(ext);
    });

    if (xlsCandidates.length === 0) {
        return { error: 'No XLSX file found in the selected folder.' };
    }

    xlsCandidates.sort((a, b) => a.name.localeCompare(b.name));
    const xlsFile = xlsCandidates[0];

    const rowCount = await countXlsDataRows(xlsFile);
    if (rowCount === null) {
        return { error: 'XLSX file could not be parsed.' };
    }

    const imageFiles = all.filter((f) => f !== xlsFile && isImageFilename(f.name));

    imageFiles.sort((a, b) => a.name.localeCompare(b.name));

    const warnings: PreflightWarning[] = [];
    const matched: PreparedFile[] = [];

    // First pass: validate each image and group surviving candidates by row.
    // Validation failures emit individual warnings; survivors are bucketed
    // per row so duplicates can be reported as a single combined warning.
    const candidates: Record<number, File[]> = {};

    for (const file of imageFiles) {
        const rowIndex = parseRowIndex(file.name);

        if (rowIndex === null) {
            warnings.push({
                kind: 'invalid-name',
                text: `${file.name}: filename must start with {row_number}.{...}.{ext}`,
                relatedFile: file.name,
            });
            continue;
        }

        if (!isWithinSizeLimit(file)) {
            warnings.push({
                kind: 'too-large',
                text: `${file.name}: larger than 1 MB or empty, skipped`,
                relatedFile: file.name,
                rowIndex,
            });
            continue;
        }

        const okMagic = await checkMagicBytes(file);
        if (!okMagic) {
            warnings.push({
                kind: 'unsupported-format',
                text: `${file.name}: file contents do not match jpg/png/bmp`,
                relatedFile: file.name,
                rowIndex,
            });
            continue;
        }

        const okDecode = await isImageDecodable(file);
        if (!okDecode) {
            warnings.push({
                kind: 'corrupted',
                text: `${file.name}: file is corrupted or unreadable`,
                relatedFile: file.name,
                rowIndex,
            });
            continue;
        }

        if (!candidates[rowIndex]) candidates[rowIndex] = [];
        candidates[rowIndex].push(file);
    }

    const lastRow = DATA_START_ROW + rowCount - 1;
    const rowKeys = Object.keys(candidates)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b);

    for (const rowIndex of rowKeys) {
        const group = candidates[rowIndex];
        // Stable order so the "winner" choice is predictable and matches the
        // backend (which also sorts by filename).
        group.sort((a, b) => a.name.localeCompare(b.name));
        const winner = group[0];
        const losers = group.slice(1);

        if (losers.length > 0) {
            const loserNames = losers.map((f) => f.name).join(', ');
            warnings.push({
                kind: 'duplicate-prefix',
                text: `Row ${rowIndex}: ${group.length} files found (${group.map((f) => f.name).join(', ')}). Using ${winner.name}, skipping ${loserNames}.`,
                rowIndex,
            });
        }

        if (rowIndex < DATA_START_ROW || rowIndex > lastRow) {
            warnings.push({
                kind: 'no-row',
                text: `${winner.name}: XLS data rows are ${DATA_START_ROW}-${lastRow}, prefix ${rowIndex} has no matching row`,
                relatedFile: winner.name,
                rowIndex,
            });
        }

        const sha256 = await sha256OfFile(winner);
        matched.push({ file: winner, rowIndex, sha256 });
    }

    const matchedRows = new Set(matched.map((m) => m.rowIndex));
    for (let i = DATA_START_ROW; i <= lastRow; i++) {
        if (!matchedRows.has(i)) {
            warnings.push({
                kind: 'no-image',
                text: `Row ${i}: no image file found in folder`,
                rowIndex: i,
            });
        }
    }

    return { xlsFile, rowCount, matched, warnings };
}
